import React from 'react';
import useReport from '../lib/useReport';
import { useFilters } from '../contexts/FilterContext';
import { money, int, niceDate } from '../lib/format';
import { Card, Spinner, ErrorBox, DataTable, Empty } from '../components/ui';

// Where margin leaks: which discounts are used, how often, and what refunds
// each location is issuing.
export default function DiscountsPage() {
  const { reportParams, startDate, endDate } = useFilters();
  const { data, loading, error } = useReport('discounts-refunds', reportParams);

  if (error) return <ErrorBox message={error} />;
  if (loading || !data) return <Spinner label="Tallying discounts…" />;

  const { discounts, charges, refundsByLocation } = data;

  return (
    <div className="grid" style={{ gap: 14 }}>
      <div className="grid two-col">
        <Card tour="disc-discounts" title="Discounts Applied" sub={`${niceDate(startDate)} – ${niceDate(endDate)} · by discount label`}>
          {discounts.length === 0 ? <Empty>No discounts in this period.</Empty> : (
            <DataTable
              csvName="discounts.csv"
              defaultSort={{ key: 'amount', dir: 'desc' }}
              maxRows={15}
              columns={[
                { key: 'label', label: 'Discount' },
                { key: 'uses', label: 'Uses', num: true, render: r => int(r.uses), csv: r => r.uses, footer: rows => int(rows.reduce((a, r) => a + r.uses, 0)) },
                { key: 'amount', label: 'Amount', num: true, render: r => money(r.amount), csv: r => (r.amount / 100).toFixed(2), footer: rows => money(rows.reduce((a, r) => a + r.amount, 0)) },
                {
                  key: 'avg', label: 'Avg', num: true,
                  sortValue: r => (r.uses ? r.amount / r.uses : 0),
                  render: r => r.uses ? money(r.amount / r.uses) : '—',
                  csv: r => r.uses ? (r.amount / r.uses / 100).toFixed(2) : '',
                },
              ]}
              rows={discounts}
              footer
            />
          )}
        </Card>

        <Card tour="disc-charges" title="Added Charges" sub="delivery fees, service charges, etc.">
          {charges.length === 0 ? <Empty>No charges in this period.</Empty> : (
            <DataTable
              csvName="charges.csv"
              defaultSort={{ key: 'amount', dir: 'desc' }}
              maxRows={15}
              columns={[
                { key: 'label', label: 'Charge' },
                { key: 'uses', label: 'Uses', num: true, render: r => int(r.uses), csv: r => r.uses },
                { key: 'amount', label: 'Amount', num: true, render: r => money(r.amount), csv: r => (r.amount / 100).toFixed(2), footer: rows => money(rows.reduce((a, r) => a + r.amount, 0)) },
              ]}
              rows={charges}
              footer
            />
          )}
        </Card>
      </div>

      <Card tour="disc-refunds" title="Refunds by Location">
        {refundsByLocation.length === 0 ? <Empty>No refunds in this period. 🎉</Empty> : (
          <DataTable
            csvName="refunds-by-location.csv"
            defaultSort={{ key: 'amount', dir: 'desc' }}
            columns={[
              { key: 'locationName', label: 'Location' },
              { key: 'refunds', label: 'Refunds', num: true, render: r => int(r.refunds), csv: r => r.refunds },
              { key: 'amount', label: 'Amount', num: true, render: r => money(r.amount), csv: r => (r.amount / 100).toFixed(2), footer: rows => money(rows.reduce((a, r) => a + r.amount, 0)) },
            ]}
            rows={refundsByLocation.map(r => ({ ...r, __key: r.locationId }))}
            footer
          />
        )}
      </Card>
    </div>
  );
}
